import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { KeycloakService } from 'keycloak-angular';
import { EMPTY, of, timer } from 'rxjs';
import { catchError, delay, exhaustMap, filter, map, scan, switchMap, take, takeUntil, timeout } from 'rxjs/operators';

import { isKioskUrl } from '@core/helpers/is-kiosk-url';
import { refreshAccessToken$ } from '@core/helpers/refresh-access-token';
import { WebsocketService } from '@core/services/websocket.service';

import { cancelOnAction } from '@shared/operators/cancel-on-action.operator';

import { logout } from '../auth/auth.actions';
import { selectAccessToken } from '../auth/auth.selectors';
import {
  authenticate,
  authenticateFailure,
  authenticateRejected,
  authenticateSuccess,
  connectToWebsocket,
  connectToWebsocketSuccess,
  reconnectToWebsocketIsSafe,
  showConnectionLostOverlay,
  websocketClosed,
  websocketConnectionLost,
  websocketFailure,
} from './websocket.actions';
import {
  CONNECTION_LOST_OVERLAY_GRACE_MS,
  HEARTBEAT_INTERVAL_MS,
  HEARTBEAT_MAX_CONSECUTIVE_MISSES,
  HEARTBEAT_RESPONSE_TIMEOUT_MS,
  WEBSOCKET_AUTHENTICATION_REJECTED,
  WEBSOCKET_RECONNECT_DELAY_MS,
} from './websocket.constants';

@Injectable()
export class WebsocketEffects {
  readonly #actions$ = inject(Actions);
  readonly #store = inject(Store);
  readonly #websocketService = inject(WebsocketService);
  readonly #keycloakService = inject(KeycloakService);

  public connectToWebsocket$ = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(connectToWebsocket, websocketFailure, websocketClosed),
        switchMap((action) =>
          timer(action.type === connectToWebsocket.type ? 0 : WEBSOCKET_RECONNECT_DELAY_MS).pipe(
            map(() => {
              this.#websocketService.connect();
            })
          )
        )
      ),
    {
      dispatch: false,
    }
  );

  public authenticateOnConnectionSuccess$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(connectToWebsocketSuccess),
      switchMap(() => {
        return this.#store.select(selectAccessToken).pipe(
          filter((accessToken): accessToken is string => !!accessToken),
          take(1)
        );
      }),
      map((accessToken) => {
        return authenticate({ accessToken });
      })
    )
  );

  public authenticate$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(authenticate),
      switchMap(({ accessToken, isRetry }) =>
        this.#websocketService.authenticate$(accessToken).pipe(
          map(() => authenticateSuccess()),
          catchError((error) => {
            if (error === WEBSOCKET_AUTHENTICATION_REJECTED && !isRetry) {
              return of(authenticateRejected());
            }

            return of(
              authenticateFailure({
                error: 'Failed to authenticate websocket connection',
              })
            );
          })
        )
      ),
      cancelOnAction(this.#actions$, [connectToWebsocket, websocketFailure, websocketClosed])
    )
  );

  public refreshTokenAndRetryAuthentication$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(authenticateRejected),
      switchMap(() =>
        refreshAccessToken$(this.#keycloakService, this.#store).pipe(
          map((accessToken) => {
            if (!accessToken) {
              // Keycloak cleared the tokens (e.g. expired refresh token) — the session is gone
              this.#store.dispatch(logout());

              return authenticateFailure({ error: 'No access token after refresh' });
            }

            return authenticate({ accessToken, isRetry: true });
          }),
          catchError(() => {
            this.#store.dispatch(logout());

            return of(authenticateFailure({ error: 'Failed to refresh access token' }));
          })
        )
      ),
      cancelOnAction(this.#actions$, [connectToWebsocket, websocketFailure, websocketClosed])
    )
  );

  public heartbeat$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(authenticateSuccess),
      switchMap(() =>
        timer(HEARTBEAT_INTERVAL_MS, HEARTBEAT_INTERVAL_MS).pipe(
          exhaustMap(() =>
            this.#websocketService.sendHeartbeat$().pipe(
              timeout(HEARTBEAT_RESPONSE_TIMEOUT_MS),
              map(() => false),
              catchError(() => of(true))
            )
          ),
          scan((consecutiveMisses, missed) => (missed ? consecutiveMisses + 1 : 0), 0),
          filter((consecutiveMisses) => consecutiveMisses >= HEARTBEAT_MAX_CONSECUTIVE_MISSES),
          take(1),
          map(() => websocketFailure({ error: 'Websocket heartbeat timed out' })),
          takeUntil(this.#actions$.pipe(ofType(websocketClosed, websocketFailure, connectToWebsocket)))
        )
      )
    )
  );

  // connectToWebsocketSuccess in the source cancels a pending grace timer via switchMap
  public websocketConnectionLost$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(websocketClosed, websocketFailure),
      map(() => websocketConnectionLost())
    )
  );

  public showConnectionLostOverlay$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(websocketConnectionLost, connectToWebsocketSuccess),
      switchMap((action) =>
        action.type === connectToWebsocketSuccess.type
          ? EMPTY
          : timer(CONNECTION_LOST_OVERLAY_GRACE_MS).pipe(
              filter(() => !isKioskUrl()),
              map(() => showConnectionLostOverlay())
            )
      )
    )
  );

  public emitReconnectToWebsocketIsSafe$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(websocketFailure),
      switchMap(() => {
        return this.#actions$.pipe(ofType(authenticateSuccess));
      }),
      delay(0),
      map(() => {
        return reconnectToWebsocketIsSafe();
      })
    )
  );
}
